import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../App';
import SnapshotExplorer from '../components/SnapshotExplorer';
import { formatDateTime, DEFAULT_TZ, useIsMobile, useServerHeartbeats, liveHeartbeat, WsWarnBadge } from '../utils';

// ─── Calendar helpers ──────────────────────────────────────────────────────

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth() &&
         a.getDate()     === b.getDate();
}

function startOfMonth(y, m) { return new Date(y, m, 1); }
function daysInMonth(y, m)  { return new Date(y, m + 1, 0).getDate(); }

// ─── SnapshotCalendar ──────────────────────────────────────────────────────

function SnapshotCalendar({ server, onSelectRun, isMobile = false }) {
  const [year, setYear]       = useState(() => new Date().getFullYear());
  const [month, setMonth]     = useState(() => new Date().getMonth());
  const [snapshots, setSnaps]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [dayRuns, setDayRuns]   = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // { year, month, day }

  useEffect(() => {
    setLoading(true);
    setSnaps([]);
    setDayRuns(null);
    api.getServerSnapshots(server.id)
      .then(setSnaps)
      .catch(() => setSnaps([]))
      .finally(() => setLoading(false));
  }, [server.id]);

  const snapshotsByDay = snapshots.reduce((acc, r) => {
    if (!r.started_at) return acc;
    const d = new Date(r.started_at);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setDayRuns(null);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setDayRuns(null);
    setSelectedDay(null);
  };

  const handleDayClick = (day) => {
    const key = `${year}-${month}-${day}`;
    const runs = snapshotsByDay[key];
    if (!runs || runs.length === 0) return;
    setSelectedDay({ year, month, day });
    setDayRuns({ day, runs });
  };

  const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                       'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const DAY_NAMES   = ['Lu','Ma','Mi','Ju','Vi','Sa','Do'];

  const firstDow = (startOfMonth(year, month).getDay() + 6) % 7; // Mon=0
  const totalDays = daysInMonth(year, month);
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const today = new Date();

  return (
    <div>
      {/* Calendar header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={prevMonth} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 10px', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 13 }}>‹</button>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 14, flex: 1, textAlign: 'center' }}>
          {MONTH_NAMES[month]} {year}
        </span>
        <button onClick={nextMonth} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 10px', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 13 }}>›</button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 12 }}>
          Cargando puntos de restauración...
        </div>
      )}

      {!loading && (
        <>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
            {DAY_NAMES.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', padding: '4px 0' }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} />;
              const key        = `${year}-${month}-${day}`;
              const runs       = snapshotsByDay[key] || [];
              const hasSnap    = runs.length > 0;
              const isSelected = selectedDay?.year === year &&
                                 selectedDay?.month === month &&
                                 selectedDay?.day === day;
              const isTodayCell = isSameDay(new Date(year, month, day), today);

              const borderStyle = isSelected
                ? '2px solid var(--accent)'
                : isTodayCell
                  ? '1px solid var(--accent)'
                  : '1px solid transparent';

              const bgStyle = isSelected
                ? 'rgba(0,212,255,0.15)'
                : hasSnap
                  ? 'rgba(0,229,160,0.08)'
                  : 'transparent';

              return (
                <div
                  key={day}
                  onClick={() => hasSnap && handleDayClick(day)}
                  style={{
                    textAlign: 'center', padding: isMobile ? '14px 2px' : '10px 4px', borderRadius: 3,
                    fontSize: 12, fontFamily: 'var(--mono)',
                    cursor: hasSnap ? 'pointer' : 'default',
                    background: bgStyle,
                    border: borderStyle,
                    color: isSelected ? 'var(--accent)' : hasSnap ? 'var(--green)' : 'var(--muted)',
                    fontWeight: (isSelected || hasSnap) ? 700 : 400,
                    position: 'relative',
                  }}
                  title={hasSnap ? `${runs.length} snapshot${runs.length > 1 ? 's' : ''}` : undefined}
                >
                  {day}
                  {hasSnap && (
                    <div style={{
                      position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)',
                      width: 5, height: 5, borderRadius: '50%',
                      background: isSelected ? 'var(--accent)' : 'var(--green)',
                    }} />
                  )}
                </div>
              );
            })}
          </div>

          {snapshots.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 12, marginTop: 8 }}>
              Sin puntos de restauración para este servidor
            </div>
          )}

          {/* Snapshot list */}
          {dayRuns && (
            <div style={{ marginTop: 16, border: '1px solid var(--border)', background: 'var(--bg)', padding: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', letterSpacing: 2, marginBottom: 10 }}>
                PUNTOS DE RESTAURACIÓN — {String(dayRuns.day).padStart(2,'0')}/{String(month+1).padStart(2,'0')}/{year}
              </div>
              {dayRuns.runs.map(r => (
                <div
                  key={r.id}
                  onClick={() => onSelectRun(r)}
                  style={{
                    padding: '10px 12px', marginBottom: 4, cursor: 'pointer',
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    fontFamily: 'var(--mono)', fontSize: 12,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <span>{r.job_name}</span>
                  <span style={{ color: 'var(--muted)', fontSize: 11 }}>
                    {r.started_at
                      ? new Date(r.started_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
                      : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── ClientTree ────────────────────────────────────────────────────────────

function ClientTree({ clients, selectedServer, onSelectServer, heartbeatFeed }) {
  const [expanded, setExpanded] = useState({});
  const [serverMap, setServerMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});

  const toggleClient = async (clientId) => {
    const isOpen = !!expanded[clientId];
    if (isOpen) {
      setExpanded(e => ({ ...e, [clientId]: false }));
      return;
    }
    setExpanded(e => ({ ...e, [clientId]: true }));
    if (!serverMap[clientId]) {
      setLoadingMap(l => ({ ...l, [clientId]: true }));
      try {
        const servers = await api.getServers(clientId);
        setServerMap(s => ({ ...s, [clientId]: servers }));
      } catch {
        setServerMap(s => ({ ...s, [clientId]: [] }));
      } finally {
        setLoadingMap(l => ({ ...l, [clientId]: false }));
      }
    }
  };

  return (
    <div>
      {clients.map(c => {
        const isOpen    = !!expanded[c.id];
        const servers   = serverMap[c.id] || [];
        const isLoading = !!loadingMap[c.id];
        return (
          <div key={c.id} style={{ marginBottom: 2 }}>
            {/* Client row */}
            <div
              onClick={() => toggleClient(c.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', cursor: 'pointer', borderRadius: 3,
                background: isOpen ? 'rgba(0,212,255,0.06)' : 'transparent',
                border: '1px solid transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = isOpen ? 'rgba(0,212,255,0.06)' : 'transparent'}
            >
              <span style={{ color: 'var(--accent)', fontSize: 10 }}>{isOpen ? '▼' : '▶'}</span>
              <span style={{ fontSize: 13, fontFamily: 'var(--mono)', flex: 1 }}>{c.name}</span>
              {c.internal && (
                <span style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: 1, border: '1px solid var(--border)', padding: '1px 4px' }}>INT</span>
              )}
            </div>

            {/* Server rows */}
            {isOpen && (
              <div style={{ marginLeft: 16 }}>
                {isLoading && (
                  <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>cargando...</div>
                )}
                {!isLoading && servers.length === 0 && (
                  <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Sin servidores</div>
                )}
                {servers.map(s => {
                  const isSel = selectedServer?.id === s.id;
                  const hb = liveHeartbeat(s, heartbeatFeed);
                  return (
                    <div
                      key={`${c.id}-${s.id}`}
                      onClick={() => onSelectServer(s, c)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 10px', cursor: 'pointer', borderRadius: 3,
                        background: isSel ? 'rgba(0,229,160,0.1)' : 'transparent',
                        border: isSel ? '1px solid var(--green)' : '1px solid transparent',
                        marginBottom: 1,
                      }}
                    >
                      <span style={{ fontSize: 11 }}>⬡</span>
                      <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: isSel ? 'var(--green)' : 'var(--text)' }}>
                        {s.hostname}
                      </span>
                      {hb.wsWarning && <WsWarnBadge style={{ marginLeft: 'auto' }} />}
                      <span title={hb.label} style={{
                        marginLeft: hb.wsWarning ? 4 : 'auto', width: 7, height: 7, borderRadius: '50%',
                        background: hb.color,
                        flexShrink: 0,
                      }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// #213 — antes se probaba la delegación con un api.listSnapshot, que mandaba un
// list_snapshot real al agente (y el explorador lo repetía al abrirse). Se
// consulta la delegación sin tocar al agente; si no se puede verificar, abre el
// explorador, que muestra el error que corresponda (410, agente offline, 403).
export async function openRunIfDelegated(clientId, run, { setPermError, setExplorer }) {
  setPermError(false);
  try {
    const d = await api.getDelegationCheck(clientId);
    if (!d?.can_explore) { setPermError(true); return; }
  } catch {
    // sin verificación previa: lo resuelve el explorador
  }
  setExplorer(run);
}

// ─── Últimas restauraciones (#168) ─────────────────────────────────────────

const RESTORE_STATUS_LABEL = { pending: 'En cola', running: 'En curso', ok: 'Restaurado', error: 'Falló', rejected: 'Rechazada', cancelled: 'Cancelada', pending_manual: 'Pendiente' };
const RESTORE_STATUS_COLOR = { ok: 'var(--green)', error: 'var(--red)', pending: 'var(--accent)', running: 'var(--accent)' };

export function RecentRestores({ isMobile = false }) {
  const [rows, setRows]       = useState(null);
  const [failed, setFailed]   = useState(false);

  useEffect(() => {
    api.getRestores().then(setRows).catch(() => { setRows([]); setFailed(true); });
  }, []);

  const statusCell = (r) => (
    <span title={r.error_msg || undefined} style={{ color: RESTORE_STATUS_COLOR[r.status] || 'var(--muted)', textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
      {RESTORE_STATUS_LABEL[r.status] || r.status}
    </span>
  );
  const errorLine = (r) => r.status === 'error' && r.error_msg
    ? <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4, wordBreak: 'break-word' }}>{r.error_msg}</div>
    : null;

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 12 }}>
        Últimas restauraciones
      </div>
      {rows === null ? (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>Cargando...</div>
      ) : rows.length === 0 ? (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>
          {failed ? 'No se pudieron cargar las restauraciones' : 'Sin restauraciones todavía'}
        </div>
      ) : isMobile ? (
        rows.map(r => (
          <div key={r.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '12px 14px', marginBottom: 8, fontFamily: 'var(--mono)', fontSize: 12 }}>
            <div style={{ color: 'var(--muted)', fontSize: 11 }}>{formatDateTime(r.requested_at, DEFAULT_TZ)}</div>
            <div style={{ margin: '4px 0' }}>{r.server} — {r.job_name}</div>
            <div style={{ color: 'var(--muted)', wordBreak: 'break-all' }}>{r.target_path}</div>
            <div style={{ color: 'var(--muted)', fontSize: 11 }}>{r.requested_by}</div>
            <div style={{ marginTop: 6 }}>{statusCell(r)}</div>
            {errorLine(r)}
          </div>
        ))
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--mono)' }}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                {['Fecha', 'Servidor', 'Job', 'Destino', 'Quién', 'Estado'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', color: 'var(--muted)' }}>{formatDateTime(r.requested_at, DEFAULT_TZ)}</td>
                  <td style={{ padding: '10px 14px' }}>{r.server}</td>
                  <td style={{ padding: '10px 14px' }}>{r.job_name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--muted)', wordBreak: 'break-all' }}>{r.target_path}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--muted)' }}>{r.requested_by}</td>
                  <td style={{ padding: '10px 14px' }}>{statusCell(r)}{errorLine(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

export default function Restores() {
  const { isAdmin, isOperator, isReseller, isClient } = useAuth();
  const isMobile = useIsMobile();
  const [clients, setClients]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedServer, setSelectedServer] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [explorer, setExplorer]         = useState(null); // run object
  const [permError, setPermError]       = useState(false);
  const [showTree, setShowTree]         = useState(true);
  const [selectedClientServers, setSelectedClientServers] = useState([]);
  const heartbeatFeed = useServerHeartbeats();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const c = await api.getClients(isAdmin || isOperator);
        setClients(c);
      } catch {
        setClients([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // #149 — servidores del cliente seleccionado, para el selector de destino
  // de SnapshotExplorer (ClientTree mantiene su propio caché interno, no
  // accesible desde acá).
  useEffect(() => {
    if (!selectedClient?.id) { setSelectedClientServers([]); return; }
    api.getServers(selectedClient.id).then(setSelectedClientServers).catch(() => setSelectedClientServers([]));
  }, [selectedClient?.id]);

  const handleSelectServer = (server, client) => {
    setSelectedServer(server);
    setSelectedClient(client);
    setExplorer(null);
    setPermError(false);
  };

  const handleSelectRun = (run) => openRunIfDelegated(selectedClient?.id, run, { setPermError, setExplorer });

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
      CARGANDO...
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ padding: '0 0 80px 0' }}>

        {/* Show tree when no server selected, or when showTree forced */}
        {(!selectedServer || showTree) ? (
          <div style={{ padding: '16px 12px' }}>
            <div style={{
              fontSize: 10, letterSpacing: 2, color: 'var(--muted)',
              fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 12
            }}>
              Clientes y Servidores
            </div>
            <ClientTree
              clients={clients}
              heartbeatFeed={heartbeatFeed}
              selectedServer={selectedServer}
              onSelectServer={(s, c) => {
                handleSelectServer(s, c);
                setShowTree(false);
              }}
            />
          </div>
        ) : (
          <div style={{ padding: '12px 12px' }}>
            <button
              onClick={() => {
                setShowTree(true);
                setExplorer(null);
                setPermError(false);
              }}
              style={{
                background: 'transparent', border: 'none',
                color: 'var(--accent)', fontFamily: 'var(--mono)',
                fontSize: 12, cursor: 'pointer', marginBottom: 16, padding: 0
              }}
            >
              ← Volver
            </button>

            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 10, letterSpacing: 2, color: 'var(--muted)',
                fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 4
              }}>
                {selectedClient?.name}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 16 }}>
                ⬡ {selectedServer.hostname}
              </div>
            </div>

            {permError && (
              <div style={{
                marginBottom: 16, padding: '12px 16px',
                background: 'rgba(255,200,0,0.08)',
                border: '1px solid var(--yellow)',
                color: 'var(--yellow)', fontFamily: 'var(--mono)', fontSize: 12
              }}>
                🔒 El cliente no ha delegado los permisos de
                exploración/restauración. Para proceder, el cliente debe
                activar la delegación desde su panel.
              </div>
            )}

            <SnapshotCalendar
              key={selectedServer.id}
              server={selectedServer}
              onSelectRun={handleSelectRun}
              isMobile={true}
            />
          </div>
        )}

        <div style={{ padding: '0 12px' }}><RecentRestores isMobile={true} /></div>

        {explorer && (
          <SnapshotExplorer
            run={explorer}
            serverId={explorer.server_id}
            server={selectedServer}
            onClose={() => { setExplorer(null); setPermError(false); }}
            heartbeatFeed={heartbeatFeed}
            clientServers={selectedClientServers}
            allowCrossServerRestore={!!selectedClient?.allow_cross_server_restore}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 80px)', overflow: 'hidden' }}>

      {/* LEFT PANEL — client/server tree */}
      <div style={{
        width: 260, minWidth: 220, flexShrink: 0,
        borderRight: '1px solid var(--border)',
        overflowY: 'auto', padding: '16px 8px',
      }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 12, padding: '0 8px' }}>
          Clientes y Servidores
        </div>
        {clients.length === 0 && (
          <div style={{ padding: '0 10px', fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Sin clientes</div>
        )}
        <ClientTree
          clients={clients}
          heartbeatFeed={heartbeatFeed}
          selectedServer={selectedServer}
          onSelectServer={handleSelectServer}
        />
      </div>

      {/* RIGHT PANEL — calendar or placeholder */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {!selectedServer && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 32, opacity: 0.2 }}>📅</div>
            <div style={{ fontFamily: 'var(--mono)', color: 'var(--muted)', fontSize: 13 }}>
              Seleccioná un servidor para ver sus puntos de restauración
            </div>
          </div>
        )}

        {selectedServer && (
          <>
            {/* Server header */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 4 }}>
                {selectedClient?.name}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 18 }}>
                ⬡ {selectedServer.hostname}
              </div>
            </div>

            {/* Permission warning */}
            {permError && (
              <div style={{
                marginBottom: 16, padding: '12px 16px',
                background: 'rgba(255,200,0,0.08)', border: '1px solid var(--yellow)',
                color: 'var(--yellow)', fontFamily: 'var(--mono)', fontSize: 12,
              }}>
                🔒 El cliente no ha delegado los permisos de exploración/restauración.
                Para proceder, el cliente debe activar la delegación desde su panel.
              </div>
            )}

            {/* Calendar */}
            <div style={{ maxWidth: 420 }}>
              <SnapshotCalendar
                key={selectedServer.id}
                server={selectedServer}
                onSelectRun={handleSelectRun}
                isMobile={false}
              />
            </div>
          </>
        )}
        <RecentRestores />
      </div>

      {/* Snapshot Explorer overlay */}
      {explorer && (
        <SnapshotExplorer
          run={explorer}
          serverId={explorer.server_id}
          server={selectedServer}
          onClose={() => { setExplorer(null); setPermError(false); }}
          heartbeatFeed={heartbeatFeed}
          clientServers={selectedClientServers}
          allowCrossServerRestore={!!selectedClient?.allow_cross_server_restore}
        />
      )}
    </div>
  );
}
